import { NAVIGATION_HOTSPOT_SURFACES } from "./worldJson";

const MAX_CHARS = 1800;

const SURFACE_LABELS = {
  desk: ["document", "documents"],
  table: ["document", "documents"],
  bookshelf: ["saved item", "saved items"],
  calendar: ["calendar event", "calendar events"],
  bulletin_board: ["to-do", "to-dos"],
  bulletin: ["to-do", "to-dos"],
  whiteboard: ["whiteboard card", "whiteboard cards"],
};

const clip = (text, n) => {
  const s = String(text || "").replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
  return s.length > n ? `${s.slice(0, n - 1)}…` : s;
};

function isRealWork(item, room) {
  if (!item?.surface || NAVIGATION_HOTSPOT_SURFACES.has(item.surface)) return false;
  const placeholderTitle = room?.name ? `${room.name}: ${item.surface}` : item.surface;
  if (!String(item.content || "").trim() && (!item.title || item.title === placeholderTitle)) return false;
  return true;
}

function describeItems(items) {
  const bySurface = {};
  for (const item of items) {
    (bySurface[item.surface] = bySurface[item.surface] || []).push(item);
  }
  return Object.entries(bySurface).map(([surface, list]) => {
    const [one, many] = SURFACE_LABELS[surface] || [surface, surface];
    const names = list.slice(0, 2)
      .map((i) => clip(i.title && i.title !== "Untitled" ? i.title : i.content, 50))
      .filter(Boolean)
      .map((n) => `"${n}"`);
    const done = list.filter((i) => i.done).length;
    const count = `${list.length} ${list.length === 1 ? one : many}`;
    const extra = [
      names.length ? names.join(", ") + (list.length > 2 ? ", …" : "") : null,
      done ? `${done} done` : null,
    ].filter(Boolean).join("; ");
    return extra ? `${count} (${extra})` : count;
  }).join(", ");
}

export function describeProject({ building, currentRoomId, roomSurfaceItems = {}, activities = [] }) {
  if (!building) return "none";

  const activityByRoom = Object.fromEntries((activities || []).map((a) => [a.room_id, a]));
  const rooms = (building.floors || []).flatMap((floor) =>
    (floor.rooms || []).map((room) => ({ room, floor })));
  if (!rooms.length) return "none";

  const lines = [`${building.name || "This building"} has ${rooms.length} room${rooms.length === 1 ? "" : "s"}:`];

  for (const { room } of rooms) {
    const here = room.id === currentRoomId;
    const items = (roomSurfaceItems[room.id] || []).filter((i) => isRealWork(i, room));
    const activity = activityByRoom[room.id];
    const parts = [];
    if (items.length) parts.push(describeItems(items));
    if (activity?.last_summary) parts.push(`Last time: ${clip(activity.last_summary, 160)}`);
    if (activity?.next_step) parts.push(`Next step: ${clip(activity.next_step, 100)}`);
    if (!parts.length) parts.push(activity ? "visited, nothing made yet" : "not started yet");
    const sentence = parts.map((p) => p.replace(/[.\s]+$/, "")).join(". ");
    lines.push(`- ${room.name || "Room"}${here ? " (the user is here)" : ""}: ${sentence}`);
  }

  let out = "";
  for (const line of lines) {
    if (out.length + line.length + 1 > MAX_CHARS) {
      out += "\n- …and more rooms";
      break;
    }
    out += (out ? "\n" : "") + line;
  }
  return out;
}
